'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CodeSetPreview,
  ColumnSelectorDropdown,
  Drawer,
  NewVolumeTypeToggle,
  ProspectModeToggle,
  UnsavedChangesDialog,
} from 'src/components';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import useTableFilterSelections from 'src/hooks/filters/useTableFilterSelections';
import {
  useAppDispatch,
  activateLocation,
  setTempSelectedFilterPreset,
} from 'src/redux';
import ColumnFilterWrapper from './ColumnFilterWrapper';
import { YearRangeDropdown, ViewTrendButton } from '../components';
import { IconButton } from 'src/components/PulseUI/IconButton';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';

interface TableVolumeDrawerProps {
  location: string;
  showReferringTreating?: boolean;
  showBilling?: boolean;
  showTrending?: boolean;
  defaultMode?: 'treating' | 'sending';
}

const TableVolumeDrawer = ({
  location,
  showReferringTreating = false,
  showBilling = false,
  showTrending = false,
  defaultMode,
}: TableVolumeDrawerProps) => {
  const dispatch = useAppDispatch();
  const { isDrawerOpen, closeDrawer } = useDrawer();
  const { clearAllFilters, isDirty, applyFilters } = useFilterSets(location);

  // Activate location when drawer opens
  const drawerOpen = isDrawerOpen(`table-volume-${location}`);
  useEffect(() => {
    if (drawerOpen) {
      dispatch(activateLocation(location));
    }
  }, [dispatch, location, drawerOpen]);

  const {
    columnId,
    clearFilterType,
    clearBilling,
    clearColumnFilters,
    applySelections,
    removeFilter,
    allAppliedColumnSettings,
    allTempColumnSettings,
    isSelectionsDirty,
    // Add trending states for duplicate detection
    isTrending,
    tempIsTrending,
    editingColumnId, // This tells us which column we originally started editing
  } = useTableFilterSelections(location);

  // Track columns that we've already cleared filters for
  const clearedColumnsRef = useRef<Set<string>>(new Set());

  // Effect to clear filters for newly added columns
  useEffect(() => {
    // Only run when columnId changes and is not null
    if (columnId) {
      // Check if this is a newly added column (not in applied settings)
      const isNewColumn = !Object.keys(allAppliedColumnSettings).includes(
        columnId
      );

      // If it's a new column and we haven't cleared its filters yet
      if (isNewColumn && !clearedColumnsRef.current.has(columnId)) {
        clearColumnFilters();
        // Mark this column as having had its filters cleared
        clearedColumnsRef.current.add(columnId);
      }
    }
  }, [columnId, allAppliedColumnSettings, clearColumnFilters]);

  // Simplified duplicate detection using deep equality check
  // With instance IDs, we just need to compare actual settings objects
  const wouldCreateDuplicate = useMemo(() => {
    if (!columnId) return false;

    // If we're EDITING an existing column (editingColumnId exists), allow changes
    // Duplicate detection should only block ADDING new columns, not editing existing ones
    if (editingColumnId) {
      return false;
    }

    // If trending state has changed, it's not a duplicate
    if (tempIsTrending !== isTrending) {
      return false;
    }

    const tempSettings = allTempColumnSettings[columnId];
    if (!tempSettings) return false;

    // Check if any other applied column has identical settings
    // Using JSON.stringify for deep equality - simple and effective
    const tempSettingsJson = JSON.stringify(tempSettings);

    return Object.entries(allAppliedColumnSettings).some(
      ([, appliedSettings]) => {
        return JSON.stringify(appliedSettings) === tempSettingsJson;
      }
    );
  }, [
    columnId,
    editingColumnId,
    allTempColumnSettings,
    allAppliedColumnSettings,
    tempIsTrending,
    isTrending,
  ]);

  // Check if any changes have been made
  const isFiltersDirty = isDirty();
  const isTableSelectionsDirty = isSelectionsDirty();

  const hasChanges =
    (isFiltersDirty || isTableSelectionsDirty) && !wouldCreateDuplicate;

  const handleClose = useCallback(() => {
    closeDrawer(`table-volume-${location}`);
  }, [closeDrawer, location]);

  const handleClearFilters = () => {
    if (columnId) {
      // If a column is selected, only clear filters for that column
      clearColumnFilters();
    } else {
      // If no column is selected, clear all filters (original behavior)
      clearAllFilters('FILTER');
      // Also clear the filter set selections
      clearFilterType('professional');
      clearFilterType('education');
      clearFilterType('salesEnablement');

      // Make sure to clear billing
      clearBilling();

      // Clear only temp billing preset - applied will be cleared on Apply
      setTempSelectedFilterPreset(null);
    }
  };

  const handleApply = () => {
    applyFilters();
    applySelections();
    closeDrawer(`table-volume-${location}`);
  };

  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const handleBeforeClose = useCallback(() => {
    if (hasChanges) {
      setShowDiscardDialog(true);
    } else {
      handleClose();
    }
  }, [hasChanges, handleClose]);

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardDialog(false);
    handleClose();
  }, [handleClose]);

  const handleRemoveFilter = () => {
    // The removeFilter action already updates both temp and applied states
    // and removes all composite keys with the same base ID
    removeFilter(columnId);
    closeDrawer(`table-volume-${location}`);
  };

  return (
    <>
    <Drawer
      isOpen={isDrawerOpen(`table-volume-${location}`)}
      onClose={handleClose}
      anchor="right"
      size="base"
      hideBackdrop
      onBeforeClose={handleBeforeClose}
    >
      <Drawer.Header
        className="bg-sky-100 text-primary border-b-0"
        data-testid="table-volume-header"
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <p className="font-bold text-lg" data-testid="table-volume-title">
              Targeting Criteria
            </p>
          </div>
          <div className="flex items-center gap-2">
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              onClick={handleClose}
              icon={faXmark}
              className="text-primary hover:bg-sky-200"
              data-testid="table-volume-close-button"
            />
          </div>
        </div>
      </Drawer.Header>
      <Drawer.Content className="self-stretch p-6 bg-white inline-flex flex-col justify-start items-start gap-4">
        <div className="self-stretch inline-flex flex-col justify-start items-start gap-6">
          <div className="self-stretch flex flex-col justify-start items-start gap-4 pb-4">
            <div
              data-open="Yes"
              data-state="Default"
              className="self-stretch h-7 inline-flex justify-between items-center"
            >
              <div className="flex-1 flex justify-between items-center gap-2">
                <div className="text-center justify-center text-primary font-bold leading-6">
                  Volume
                </div>
              </div>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-1">
              <ColumnSelectorDropdown location={location} />
            </div>
            {/* Toggles */}
            {showReferringTreating && (
              <ProspectModeToggle
                location={location}
                defaultMode={defaultMode}
              />
            )}
            <NewVolumeTypeToggle location={location} />
            <div className="self-stretch inline-flex justify-start items-center gap-2 ">
              <YearRangeDropdown location={location} />
              {showTrending && <ViewTrendButton location={location} />}
            </div>
          </div>
          {showBilling && columnId ? (
            <>
              <div className="flex justify-center items-center gap-2 w-full">
                <div className="text-center justify-center text-primary font-bold leading-6">
                  Filters
                </div>
                <div
                  className="flex justify-end text-tertiary text-xs font-normal font-['Inter']
                  underline leading-none cursor-pointer hover:text-neutral-600 w-full"
                  onClick={handleClearFilters}
                >
                  Clear all
                </div>
              </div>
              <div className="self-stretch flex flex-col justify-start items-start gap-4">
                <ColumnFilterWrapper
                  location={location}
                  filterType="siteOfCare"
                  smallLabel
                />
                <ColumnFilterWrapper
                  location={location}
                  filterType="payerType"
                  smallLabel
                />
              </div>
            </>
          ) : null}
          <CodeSetPreview location={location} expandable={false} columnSelectorOnly />
        </div>
      </Drawer.Content>
      <Drawer.Footer className="w-full h-20 p-4 bg-level-1 border-t border-neutral-200 inline-flex justify-start items-center gap-4">
        <button
          className={`flex-1 px-4 py-3 rounded-full flex justify-between items-center ${
            columnId
              ? 'bg-sky-600 cursor-pointer'
              : 'bg-neutral-300 cursor-not-allowed'
          }`}
          onClick={handleRemoveFilter}
          disabled={!columnId}
        >
          <div className="flex-1 flex justify-center items-center gap-2">
            <div className="text-center justify-start text-white text-base font-semibold font-['Inter'] leading-normal">
              Remove Column
            </div>
          </div>
        </button>

        <button
          className={`flex-1 px-4 py-3 rounded-full flex justify-between items-center ${
            hasChanges && columnId
              ? 'bg-sky-600 cursor-pointer'
              : 'bg-sky-300 cursor-not-allowed'
          }`}
          onClick={hasChanges && columnId ? handleApply : undefined}
          disabled={!hasChanges || !columnId}
        >
          <div className="flex-1 flex justify-center items-center gap-2">
            <div className="text-center justify-start text-white text-base font-semibold font-['Inter'] leading-normal">
              Apply
            </div>
          </div>
        </button>
      </Drawer.Footer>
    </Drawer>
    <UnsavedChangesDialog
      isOpen={showDiscardDialog}
      onDiscard={handleDiscardConfirm}
      onCancel={() => setShowDiscardDialog(false)}
    />
  </>
  );
};

export default TableVolumeDrawer;
